import * as Api from '@ViewModels';
import { EmailType } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { useState } from 'react';
import { ISingleEmailComposerComponentProps, SingleEmailComposerKey } from '../../../../../../../../models/AppState';
import { useUserSession } from '../../../../../../../../models/hooks/appStateHooks';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../../../../components-v2/Dropdown';
import { THEME } from '../../../../../../../components-v2/Theme/theme';
import { Button } from '../../../../../../../components/Button';
import { InlineValueEditor } from '../../../../../../../components/entities/InlineValueEditor';
import { AIStarsIcon } from '../../../../../../../components/svgs/icons/AIStars';
import { EditItemPenIcon } from '../../../../../../../components/svgs/icons/EditItemPenIcon';
import { EmailIcon } from '../../../../../../../components/svgs/icons/EmailIcon';
import { PaperPlaneIcon } from '../../../../../../../components/svgs/icons/PaperPlaneIcon';
import { PrimaryIcon } from '../../../../../../../components/svgs/icons/PrimaryIcon';
import { TrashIcon } from '../../../../../../../components/svgs/icons/TrashIcon';
import { XIcon } from '../../../../../../../components/svgs/icons/XIcon';
import { PopoverType, TinyPopover } from '../../../../../../../components/TinyPopover';
import { styleSheet } from '../../styles';

interface EmailCardProps extends ISingleEmailComposerComponentProps {
	email: Api.EmailAddress;
	isPrimary?: boolean;
	contact: Api.ContactViewModel;
	onEmailChanged: (email: Api.EmailAddress) => void;
	onMakePrimaryClicked: () => void;
	onDelete: () => void;
}
const _EmailCard = ({
	email,
	isPrimary = false,
	singleEmailComposer,
	contact,
	onEmailChanged,
	onMakePrimaryClicked,
	onDelete,
}: EmailCardProps) => {
	const userSession = useUserSession();
	const [isEditing, setIsEditing] = useState(false);
	const [emailValue, setEmailValue] = useState(email.value);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean | null>(null);

	const updateEmail = () => {
		setIsEditing(false);
		onEmailChanged({ ...email, value: emailValue });
	};

	if (!email) {
		return null;
	}

	if (isEditing) {
		return (
			<InlineValueEditor
				autoComplete='off'
				autoFocus={true}
				compactLayout={true}
				id='entity-info-bullet-list-editor-input'
				onCancelButtonClicked={() => setIsEditing(false)}
				onChange={ev => setEmailValue(ev.target.value)}
				onKeyDown={ev => {
					if (ev.keyCode === 13) {
						updateEmail();
					}
				}}
				onSaveButtonClicked={updateEmail}
				value={emailValue || ''}
			/>
		);
	}

	return (
		<div className={css(styleSheet.emailCard)}>
			<div className={css(styleSheet.emailCardIcon)}>
				<EmailIcon fillColor={THEME.colors.border.main} width={18} height={18} />
			</div>
			<div className={css(styleSheet.emailCardContent)}>
				<div className={css(styleSheet.emailCardContentRow)}>
					<div>
						<a className={css(styleSheet.emailCardValue)} href={`mailto:${email.value}`}>
							{email.value}
						</a>
						{isPrimary ? (
							<div className={css(styleSheet.emailCardPrimaryLabel)}>
								<PrimaryIcon />
								<label>Primary Email</label>
							</div>
						) : null}
					</div>
					<div>
						<Button
							kind='icon'
							className={css(styleSheet.emailCardActionButton)}
							onClick={() => {
								singleEmailComposer?.showForRecipients([
									new Api.ContactViewModel(userSession, {
										...contact.toJs(),
										primaryEmail: email,
									}),
								]);
							}}
						>
							<PaperPlaneIcon style={{ color: THEME.colors.primary.main }} />
						</Button>
					</div>

					<div>
						<Dropdown onOpenChange={open => setIsDropdownOpen(open)} open={isDropdownOpen || isDeleting}>
							<DropdownEllipsisTrigger />
							<DropdownContent>
								<DropdownItem
									onClick={() => {
										singleEmailComposer?.showForRecipientWithAI(
											[
												new Api.ContactViewModel(userSession, {
													...contact.toJs(),
													primaryEmail: email,
												}),
											],
											EmailType.Generic
										);
									}}
								>
									<AIStarsIcon />
									Craft Email with AI
								</DropdownItem>
								{!isPrimary && (
									<DropdownItem onClick={onMakePrimaryClicked}>
										<PrimaryIcon />
										Make Primary Address
									</DropdownItem>
								)}
								<DropdownItem onClick={() => setIsEditing(true)}>
									<EditItemPenIcon /> Edit
								</DropdownItem>
								<DropdownItem onClick={() => setIsDeleting(true)}>
									<TinyPopover
										align='end'
										anchor={
											<div className={css(styleSheet.emailCardDeleteButton)}>
												<TrashIcon />
												<span>Delete</span>
											</div>
										}
										arrow={{
											arrowSize: 10,
										}}
										arrowStyles={[styleSheet.deleteItemConfirmationArrow]}
										dismissOnOutsideAction={true}
										isOpen={isDeleting}
										onRequestClose={() => setIsDeleting(false)}
										placement={['top']}
										type={PopoverType.blue}
									>
										<div className={css(styleSheet.deleteItemConfirmationContainer)}>
											<div
												className={css(styleSheet.closeDeleteConfirmationContainer)}
												onClick={() => setIsDeleting(false)}
											>
												<XIcon color={THEME.colors.text.light} height={12} width={12} />
											</div>
											<p>Do you want to delete the email address {email.value}?</p>
											<Button size='small' onClick={onDelete}>
												Delete
											</Button>
										</div>
									</TinyPopover>
								</DropdownItem>
							</DropdownContent>
						</Dropdown>
					</div>
				</div>
			</div>
		</div>
	);
};

const EmailCardWithObserver = observer(_EmailCard);
export const EmailCard = inject(SingleEmailComposerKey)(EmailCardWithObserver);
